// InfoTip: an (i) button beside a setting's label (P-021) whose tooltip says what the setting does,
// its YAML key and its default. It opens on hover, on keyboard focus and on a tap (which pins it
// until a second tap); Escape, or a tap anywhere else, closes it.
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from './cn';

export interface InfoTipProps {
  /** The setting's name: the button reads "About <label>". */
  label: string;
  children: ReactNode;
  /** The YAML key, shown in monospace under the text. */
  yamlKey?: string;
  /** The value used when the key is unset. */
  fallback?: string;
  /** `end` opens it leftwards, for an (i) near the right edge. */
  align?: 'start' | 'end';
  className?: string;
}

export function InfoTip({ label, children, yamlKey, fallback, align = 'start', className }: InfoTipProps) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  // Escape hides it until the pointer or focus leaves and comes back.
  const [dismissed, setDismissed] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const box = useRef<HTMLSpanElement>(null);
  // Sideways nudge that keeps the box inside the viewport (an (i) near a phone's edge).
  const [shift, setShift] = useState(0);
  const id = useId();
  const open = !dismissed && (hover || focus || pinned);

  useLayoutEffect(() => {
    if (!open || !box.current) return setShift(0);
    const r = box.current.getBoundingClientRect();
    const left = r.left - shift;
    const right = r.right - shift;
    const room = document.documentElement.clientWidth - 8;
    setShift(right > room ? Math.max(8 - left, room - right) : left < 8 ? 8 - left : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- measured once per opening.
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setPinned(false);
        setDismissed(true);
      }
    }
    function onPointer(e: PointerEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) {
        setPinned(false);
        setHover(false);
        setDismissed(true);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <span
      ref={wrap}
      className={cn('relative inline-flex align-middle', className)}
      onMouseEnter={() => {
        setHover(true);
        setDismissed(false);
      }}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        aria-label={`About ${label}`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        className={cn(
          'inline-grid size-[18px] cursor-help place-items-center rounded-full border bg-page p-0 font-sans text-[11px] font-semibold leading-none coarse:size-6',
          open ? 'border-accent text-accent-display' : 'border-hairline-strong text-ink-muted hover:border-accent hover:text-accent-display',
        )}
        onFocus={() => {
          setFocus(true);
          setDismissed(false);
        }}
        onBlur={() => setFocus(false)}
        onClick={() => {
          setDismissed(false);
          setPinned((p) => !p);
        }}
      >
        i
      </button>
      {open && (
        <span
          ref={box}
          role="tooltip"
          id={id}
          style={shift ? { transform: `translateX(${shift}px)` } : undefined}
          className={cn(
            'absolute top-[calc(100%+8px)] z-20 w-[300px] max-w-[calc(100vw-40px)] rounded-lg bg-ink-display px-3 py-2.5 text-left font-sans text-[12.5px] font-normal normal-case leading-normal tracking-normal text-page shadow-lg',
            align === 'end' ? '-right-2' : '-left-2',
          )}
        >
          {children}
          {(yamlKey || fallback) && (
            <span className="mt-1.5 block text-[11.5px] opacity-80">
              {yamlKey && <code className="font-mono text-[11.5px]">{yamlKey}</code>}
              {yamlKey && fallback && ' · '}
              {fallback && `default ${fallback}`}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
